import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { t } from '../../utils/localization/localization';

export default function OidcCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeOidcCallback } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(errorParam);
      return undefined;
    }

    // React StrictMode in development can mount/unmount/remount and re-run effects.
    // If we already have a local session token, don't try to exchange the same code/state again.
    const existingToken = window.localStorage.getItem('autobot_session_token');
    if (existingToken) {
      navigate('/cv-analyzer', { replace: true });
      return undefined;
    }

    if (!code || !state) {
      setError('missing_code_or_state');
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        await completeOidcCallback(code, state);
        if (!cancelled) navigate('/cv-analyzer', { replace: true });
      } catch (e) {
        // If we do have a token despite an error (e.g. duplicate callback exchange),
        // treat this as success to avoid a broken post-login UX.
        const token = window.localStorage.getItem('autobot_session_token');
        if (!cancelled && token) {
          navigate('/cv-analyzer', { replace: true });
          return;
        }

        if (!cancelled) setError(e?.message || 'oidc_callback_failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, completeOidcCallback, navigate]);

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            {t('auth.loggingIn', 'Signing you in')}
          </Typography>

          {error ? (
            <Typography variant="body1" color="error" align="center">
              {t('auth.loginError', 'Login Failed')}: {error}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
